/**
 * product-form.js — <product-form>
 * The single implementation of variant selection, gallery switching and AJAX add-to-cart
 * for every product section (shop page, product template).
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.variantButtons = Array.from(this.querySelectorAll('.fp-variant-btn'));
    this.thumbs = Array.from(this.querySelectorAll('.fp-gallery__thumb'));
    this.mainImage = this.querySelector('[data-main-image]');
    this.idInput = this.querySelector('[data-js-variant-input]');
    this.skuEl = this.querySelector('[data-js-sku]');
    this.bestForEl = this.querySelector('[data-js-best-for]');
    this.bestForWrap = this.querySelector('[data-best-for-wrap]');
    this.priceEl = this.querySelector('[data-js-price]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.checkoutButton = this.querySelector('[data-checkout]');
    this.badge = this.querySelector('[data-cart-badge]');
    this.message = this.querySelector('[data-cart-message]');
    this.cartUrl = this.getAttribute('data-cart-add-url') || '/cart/add.js';
    this.checkoutUrl = this.getAttribute('data-checkout-url') || '/checkout';
    this.addLabel = this.addButton ? this.addButton.querySelector('[data-add-label]') : null;
    this.defaultAddLabel = this.addLabel ? this.addLabel.textContent : '';
    this.cartCount = parseInt(this.getAttribute('data-cart-count') || '0', 10) || 0;

    this.onClick = (event) => {
      const variantBtn = event.target.closest('.fp-variant-btn');
      if (variantBtn && this.contains(variantBtn)) return this.selectVariant(variantBtn);
      const thumb = event.target.closest('.fp-gallery__thumb');
      if (thumb && this.contains(thumb)) return this.selectThumb(thumb);
      if (event.target.closest('[data-add-to-cart]')) return this.addToCart(event);
      if (event.target.closest('[data-checkout]')) return this.checkout();
    };
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    clearTimeout(this.resetTimer);
  }

  setMainImage(src, alt) {
    if (!this.mainImage || !src) return;
    this.mainImage.classList.add('is-switching');
    setTimeout(() => {
      this.mainImage.removeAttribute('srcset');
      this.mainImage.src = src;
      if (alt) this.mainImage.alt = alt;
      this.mainImage.classList.remove('is-switching');
    }, 150);
  }

  selectVariant(button) {
    this.variantButtons.forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('is-active');
    button.setAttribute('aria-pressed', 'true');
    const d = button.dataset;

    if (this.idInput) this.idInput.value = d.variantId;
    if (this.skuEl) this.skuEl.textContent = d.sku || '';
    if (this.bestForEl) this.bestForEl.textContent = d.bestFor || '';
    if (this.bestForWrap) this.bestForWrap.hidden = !d.bestFor;
    if (this.priceEl && d.price) this.priceEl.textContent = d.price;
    if (d.image) this.setMainImage(d.image, d.imageAlt);
    if (this.addButton) {
      const available = d.available === 'true';
      this.addButton.disabled = !available;
      if (this.addLabel) {
        this.defaultAddLabel = available ? this.getAttribute('data-label-add') : this.getAttribute('data-label-sold-out');
        this.addLabel.textContent = this.defaultAddLabel;
      }
    }
    try {
      const url = new URL(window.location.href);
      if (this.hasAttribute('data-sync-url')) {
        url.searchParams.set('variant', d.variantId);
        window.history.replaceState({}, '', url.toString());
      }
    } catch (e) { /* non-critical */ }
  }

  selectThumb(thumb) {
    this.thumbs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-pressed', 'false');
    });
    thumb.classList.add('is-active');
    thumb.setAttribute('aria-pressed', 'true');
    this.setMainImage(thumb.dataset.img, thumb.dataset.alt);
  }

  showMessage(text) {
    if (this.message) this.message.textContent = text || '';
  }

  async addToCart(event) {
    event.preventDefault();
    if (!this.idInput || !this.idInput.value || this.addButton.disabled) return;
    this.showMessage('');
    this.addButton.disabled = true;
    try {
      const response = await fetch(this.cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.idInput.value), quantity: 1 }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'error');

      const cart = await (await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })).json();
      this.cartCount = cart.item_count;
      if (this.badge) {
        this.badge.textContent = String(cart.item_count);
        this.badge.classList.toggle('is-visible', cart.item_count > 0);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

      this.addButton.classList.add('btn--added');
      if (this.addLabel) this.addLabel.textContent = this.getAttribute('data-label-added');
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.addButton.classList.remove('btn--added');
        if (this.addLabel) this.addLabel.textContent = this.defaultAddLabel;
      }, 2000);
    } catch (error) {
      this.showMessage(error.message && error.message !== 'error' ? error.message : (this.getAttribute('data-label-error') || ''));
    } finally {
      this.addButton.disabled = false;
    }
  }

  checkout() {
    if (this.cartCount === 0) {
      this.showMessage(this.getAttribute('data-label-empty') || '');
      return;
    }
    window.location.href = this.checkoutUrl;
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
